import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { take } from 'rxjs/operators';
import { IGruposRentabilidadeResponse } from '../../../../core/settings/grupos-rentabilidade/interfaces/grupos-rentabilidade-response.interface';
import { ToastService } from '../../../../shared/components/toast/services/toast.service';
import { DnaApi } from '../../../dnas/apis/dna.api';
import { IDNAListItem } from '../../../dnas/interfaces/dna-request.interface';
import { ILinhaHeranca, montarLinhasHeranca } from '../../utils/linhas-heranca.util';

@Component({
  selector: 'sgt-dna-select',
  templateUrl: './dna-select.component.html',
  styleUrls: ['./dna-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DnaSelectComponent implements OnInit {
  @Input() trocaDe: number | null = null;
  @Input() nomeSku = '';
  @Input() nomeDnaAtual = '';
  @Input() gruposRentabilidade: IGruposRentabilidadeResponse[] = [];

  @Output() confirmar = new EventEmitter<IDNAListItem>();
  @Output() fechar = new EventEmitter<void>();

  passo: 1 | 2 = 1;
  itens: IDNAListItem[] = [];
  selecionado: IDNAListItem | null = null;
  linhas: ILinhaHeranca[] = [];
  readonly linhasSkeleton = [1, 2, 3, 4, 5, 6];
  busca = '';
  isLoading = false;
  isLoadingParametros = false;

  get modoTroca(): boolean {
    return this.trocaDe !== null;
  }

  get titulo(): string {
    return this.modoTroca ? 'Trocar de DNA' : 'Vincular a um DNA';
  }

  get rotuloConfirmacao(): string {
    return this.modoTroca ? 'Trocar e herdar' : 'Vincular e herdar';
  }

  get notaRodape(): string {
    return this.modoTroca
      ? 'Só DNAs ativos aparecem aqui, e o DNA atual fica de fora. A troca é imediata: o SKU passa a ser avaliado pelas expectativas do novo DNA.'
      : 'Só DNAs ativos aparecem aqui. Ao vincular, este SKU passa a herdar as expectativas da família — sem sobrescrever nada.';
  }

  get itensFiltrados(): IDNAListItem[] {
    const termo = this.busca.trim().toLowerCase();
    if (!termo) {
      return this.itens;
    }
    return this.itens.filter(
      (item) =>
        item.nome.toLowerCase().includes(termo) ||
        (item.biotipoNome ?? '').toLowerCase().includes(termo)
    );
  }

  constructor(
    private dnaApi: DnaApi,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.carregar();
  }

  onBuscar(termo: string): void {
    this.busca = termo ?? '';
    this.cdr.markForCheck();
  }

  escolherDna(item: IDNAListItem): void {
    this.selecionado = item;
    this.passo = 2;
    this.carregarParametros(item.id);
  }

  voltarPasso1(): void {
    this.passo = 1;
    this.cdr.markForCheck();
  }

  onConfirmar(): void {
    if (!this.selecionado || this.selecionado.id === this.trocaDe) {
      return;
    }
    this.confirmar.emit(this.selecionado);
  }

  onFechar(): void {
    this.fechar.emit();
  }

  resumo(item: IDNAListItem): string {
    const populacao = item.expectativas?.populacaoElegivel;
    if (!populacao) {
      return item.biotipoNome;
    }

    return `${item.biotipoNome} · ${populacao.toLocaleString('pt-BR')} clientes`;
  }

  trackById(_index: number, item: IDNAListItem): number {
    return item.id;
  }

  trackLinhaByNumero(_index: number, linha: ILinhaHeranca): number {
    return linha.numero;
  }

  private carregar(): void {
    this.isLoading = true;
    this.dnaApi
      .list({ includes: ['expectativas', 'skus'] })
      .pipe(take(1))
      .subscribe({
        next: (lista) => {
          this.itens = lista
            .filter((item) => item.id !== this.trocaDe)
            .sort((a, b) => a.nome.localeCompare(b.nome));
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.toastService.error('Erro ao carregar os DNAs.');
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private carregarParametros(id: number): void {
    this.linhas = [];
    this.isLoadingParametros = true;
    this.cdr.markForCheck();

    this.dnaApi
      .fetchParametros(id)
      .pipe(take(1))
      .subscribe({
        next: ({ expectativas, ...parametros }) => {
          this.linhas = montarLinhasHeranca({
            dna: parametros,
            item: {},
            expectativas: expectativas ?? null,
            gruposRentabilidade: this.gruposRentabilidade,
          });
          this.isLoadingParametros = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.toastService.error('Erro ao carregar os parâmetros do DNA.');
          this.isLoadingParametros = false;
          this.cdr.markForCheck();
        },
      });
  }
}
